<template>
  <div :class="getClass()">
    <div>
      <span>{{ $t('total') }}: {{ bodyTotal || resultsList.length || 0 }}</span>
    </div>
    <div class="results-table-header-container">
      <tr>
        <th
          v-for="(item, index) in headerItems"
          :key="'tableHeader_' + index"
          :class="[item.name, containerCls(item)]"
          @click="setSort(item)"
        >
          <span>{{ $t(item.title) }}</span><i :class="triangleDirection(item)" />
        </th>
      </tr>
      <tr
        v-if="enableFilterBar"
        id="result-table-filter-panel"
        @keydown.enter="keydownEvent"
      >
        <th
          v-for="(item, index) in headerItems"
          :key="'headerFiler_' + index"
          :class="['search-content', item.name, containerCls(item)]"
        >
          <SearchFilter
            :inputType="item.inputType"
            :type="item.name"
            :options="getOptions(item, item.options) || []"
            :customLabel="item.customLabel"
            :secondtype="item.style"
            :onchange="changeSearchItem"
            :value="passvalue(item)"
            :multiple="item.multiple"
            :cls="`${item.style}_${item.name}`"
            :searchable="item.searchable"
          />
        </th>
      </tr>
    </div>
    <div
      ref="resultsTable"
      class="results-table-content-container"
    >
      <tr
        v-for="(item, index) in resultsList"
        :key="'tablelist_' + index"
      >
        <td
          v-for="(header, i) in headerItems"
          :key="'tableCell_' + i"
          :class="[header.name, containerCls(header)]"
        >
          <template v-if="i === 0 && withCheckbox && item.id">
            <span
              class="popover-container"
              :title="checkboxCheck(item) ? $t('hint.contact.hintEmptyContact') : null"
            >
              <input
                v-model="selectCheckbox"
                type="checkbox"
                :value="item"
                :disabled="checkboxCheck(item)"
                @change="selectTableItems && selectTableItems(selectCheckbox)"
              >
            </span>
          </template>
          <span
            :class="getItemClass(item, header)"
            @click="clickTableItem(item, header)"
          >
            {{ getData(item, header) }}
          </span>
        </td>
      </tr>
      <div
        v-if="!resultsList.length"
        class="empty-results"
      >
        {{ $t('hint.contactSearch') }}
      </div>
      <HKLLoadMore
        v-if="hasMore"
        :watchElement="$refs.resultsTable"
        v-on="$listeners"
      />
    </div>
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters, mapMutations } from 'vuex'
import mutationTypes from '@/store/mutation-types'
import HKLLoadMore from '@/controls/Loading/HKLLoadMore'
import SearchFilter from './SearchFilter'
import dateUtil from '@/utils/dateUtil'
import searchFilterOptionMixin from './searchFilterOptionMixin'
import formFullnameMixin from '@/pages/formFullnameMixin'
import { getBuildingFloorZoneMap } from '@/api/buildings/request'
import { companyType, contractorStatus, nonContractorStatus } from '@/utils/locale'
import { booleanColumns, statusColumns } from './Contains'

export default {
  name: 'ResultsTable',
  components: {
    SearchFilter,
    HKLLoadMore,
  },
  mixins: [ searchFilterOptionMixin, formFullnameMixin ],
  props: {
    enableFilterBar: {
      type: Boolean,
      default: false,
    },
    headerItems: {
      type: Array,
      default () { return [] },
    },
    bodyItems: {
      type: Array,
      default () { return [] },
    },
    bodyTotal: {
      type: Number,
      default: 0,
    },
    onchange: {
      type: Function,
      default: null,
    },
    searchItems: {
      type: Object,
      default: null,
    },
    withCheckbox: {
      type: Boolean,
    },
    selectTableItems: {
      type: Function,
      default: null,
    },
    clickItem: {
      type: Function,
      default: null,
    },
    itemAccess: {
      type: Object,
      default () { return {} },
    },
    checkboxCheck: {
      type: Function,
      default: (item) => false,
    },
    keydownEvent: {
      type: Function,
      default: () => {},
    },
    hasMore: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      Buildings: null,
      BuildingFloorZoneMap: null,
      sortByKey: 'company_name',
      companyText: 'company',
      sortAscending: true,
      resultsList: [],
      selectCheckbox: [],
      lastHeader: {
        name: 'name',
        style: 'company',
      },
      tableId: 'ccdTable',
      MAX_APLABET_STRING: '龥',
    }
  },
  computed: {
    ...mapGetters('tables', ['getTableInfoById']),
  },
  watch: {
    bodyItems () {
      if (this.lastHeader) {
        this.resultsList = this.sortAscending ? this.doSort(this.lastHeader, this.bodyItems) : this.doSort(this.lastHeader, this.bodyItems).reverse()
      } else {
        this.resultsList = this.bodyItems
      }
      this.resizeTableContentContainer()
    },
    headerItems () {
      this.resizeTableContentContainer()
    },
  },
  async mounted () {
    const { BuildingFloorZoneMap, Buildings } = await getBuildingFloorZoneMap()
    this.BuildingFloorZoneMap = BuildingFloorZoneMap
    this.Buildings = Buildings
    this.resizeTableContentContainer()
    window.addEventListener('resize', this.resizeTableContentContainer)
    const lastPosition = lodash.get(this.getTableInfoById(this.tableId), 'scrollTop', 0)
    $(`.results-table-content-container`)[0].scrollTo(0, lastPosition)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.resizeTableContentContainer)
    this.saveScrollbarPosition()
  },
  methods: {
    ...mapMutations('tables', {
      setTableInfoIntoStore: mutationTypes.TABLE_SET_TABLE_INFO,
    }),
    containerCls (item) {
      return `container_${item.style ? item.style : 'common'}_${item.name}`
    },
    changeSearchItem (item) {
      this.onchange && this.onchange(item)
    },
    getKey (header) {
      return header.style === 'company' ? 'company_' + header.name : header.name
    },
    doSort (header, list) {
      const lastOne = this.sortAscending ? this.MAX_APLABET_STRING : Number.MIN_SAFE_INTEGER
      return lodash.sortBy(list || this.resultsList, item => {
        let ret = ''
        let data
        switch (header.style) {
          case 'company':
            data = lodash.get(item, `company.${header.name}`, lastOne)
            ret = header.name === 'name' ? (data || '').toUpperCase() : data
            break
          case 'tenancy':
            const defaultValue = header.name === 'id'
              ? (this.sortAscending ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER)
              : lastOne
            data = lodash.get(item, `${header.style}.${header.name}`, defaultValue)
            if (header.name === 'floor') {
              ret = item.floors ? item.floorIndex : this.sortAscending ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER
            } else if (['tradeName', 'premises'].includes(header.name)) {
              ret = lodash.get(item, `${header.style}.${header.name}`, '').toUpperCase()
            } else {
              ret = data
            }
            break
          default:
            ret = lodash.get(item, header.name, lastOne).toString().toUpperCase()
            break
        }
        if (statusColumns.includes(header.name)) {
          ret = this.statusText(header.style, item, header.name) || lastOne
        }
        return lodash.isNumber(ret)
          ? ret
          : (lodash.isEmpty(ret.toString().trim())
            ? (this.sortAscending ? this.MAX_APLABET_STRING : '')
            : ret.toString().trim())
      })
    },
    statusText (category, item = {}, statusField = 'active') {
      if (!category || category !== this.companyText) {
        return item[statusField] ? nonContractorStatus.ACTIVE : item[statusField] === false ? nonContractorStatus.INACTIVE : ''
      }
      const coreItem = lodash.cloneDeep(item.company ? item.company : item)
      let status = ''
      switch (coreItem.type) {
        case companyType.C:
          status = coreItem[statusField] ? contractorStatus.INVITED : contractorStatus.NEW
          break
        default:
          status = coreItem[statusField] ? nonContractorStatus.ACTIVE : nonContractorStatus.INACTIVE
          break
      }
      status = this.$options.filters.customStatusLabel(status)
      return status
    },
    setSort (header) {
      if (this.sortByKey === this.getKey(header)) {
        this.sortAscending = !this.sortAscending
      } else {
        this.sortByKey = this.getKey(header)
        this.lastHeader = header
      }
      this.resultsList = this.sortAscending ? this.doSort(header, this.resultsList) : this.doSort(header, this.resultsList).reverse()
    },
    triangleDirection (header) {
      return this.sortByKey === this.getKey(header) ? (this.sortAscending ? 'triangle-up' : 'triangle-down') : ''
    },
    getData (item, header) {
      let ret = ''
      switch (header.style) {
        case 'lease':
          const leases = lodash.get(item, 'tenancy.leases', '')
          ret = leases && leases.map(lease => lease[header.name]).join(', ')
          break
        case 'company':
          ret = lodash.get(item, `${header.style}.${header.name}`, '')
          if (header.name === 'id' && ![companyType.C, companyType.T, companyType.O].includes(deep(item, 'company.type'))) {
            ret = ''
          }
          break
        case 'tenancy':
          ret = lodash.get(item, `${header.style}.${header.name}`, '')
          if (header.name === 'floor') {
            ret = lodash.get(item, 'floors')
          }
          break
        default:
          ret = lodash.get(item, header.name, '')
          break
      }
      if (header.name === 'active') {
        ret = this.statusText(header.style, item, header.name)
      }
      if (booleanColumns.includes(header.name)) {
        ret = ret ? lodash.capitalize(this.$t('yes')) : ret === false ? lodash.capitalize(this.$t('no')) : ''
      }
      if (header.name === 'name' && header.style === 'contact') {
        ret = this.displayI18nFullName(item.firstName, item.lastName)
      }
      if (header.inputType === 'date') {
        ret = dateUtil.formatDate(ret, true)
      }
      return ret
    },
    clickTableItem (item, header) {
      this.clickItem && this.clickItem(item, header)
    },
    getClass () {
      const classes = ['show-table-window', 'results-table']
      return classes
    },
    resizeTableContentContainer () {
      const isWin = navigator.platform === 'Win32' || navigator.platform === 'Win64'
      setTimeout(() => {
        const trs = $('.results-table-content-container tr')
        const totalHeight = lodash.sumBy(trs, item => $(item).height())
        const containerHeight = $('.results-table-content-container').height()
        $('.results-table-content-container').css('width', $('.results-table-header-container tr').width() + ((isWin && totalHeight >= containerHeight) ? 17 : 0))
      }, 10)
    },
    getItemClass (item, header) {
      const headerKey = header.style + '_' + header.name
      const itemAccessFunc = this.itemAccess[headerKey]
      return itemAccessFunc && !itemAccessFunc(item) ? 'no_hyper_link' : headerKey
    },
    saveScrollbarPosition () {
      const tableInfo = {
        tableId: this.tableId,
        scrollTop: $(`.results-table-content-container`)[0].scrollTop || 0,
      }
      this.setTableInfoIntoStore(tableInfo)
    },
    scrollToTop () {
      this.$refs.resultsTable.scrollTo(0, 0)
    },
  },
}
</script>

<style lang='scss' scoped>
.show-table-window {
  display: block;
  overflow-x: auto;
  overflow-y: hidden;
  height: calc(100vh - 365px);
  position: relative;
}
.without-advance {
  height: calc(100vh - 297px);
  .container_contact_createdAt,
  .container_contact_updatedAt {
    min-width: 200px !important;
    max-width: 200px !important;
    width: 200px !important;
  }
  .results-table-content-container {
    height: calc(100vh - 382px) !important;
  }
}

html[data-platform="Win32"],
html[data-platform="Win64"] {
  .results-table-header-container {
    padding-right: 17px;
  }
  .show-table-window.results-table {
    padding-right: 17px;
  }
}
.results-table {
  border-spacing: 0;
  th, td {
    padding: 10px;
    text-align: left;
    width: 180px;
    min-width: 180px;
    max-width: 180px;
    word-break: break-word;
  }

  .results-table-header-container {
    font-family: Verdana;
    font-weight: bold;
    font-size: 14px;
    color: #0C3A58;
    #result-table-filter-panel th {
      font-weight: 400;

      label {
        font-weight: 400;
      }
    }
    tr:first-child {
      th {
        border-top: 1px solid #E0E1E4;
        cursor: pointer;
        text-transform: capitalize !important;
      }
    }
  }
  .active {
    max-width: 150px;
    width: 150px;
    min-width: 150px;
  }
  .container_company_name, .container_tenancy_tradeName {
    max-width: 400px;
    width: 400px;
    min-width: 400px;
  }
  .container_tenancy_premises {
    width: 100%;
  }
  .container_contact_id, .container_company_id {
    max-width: 150px !important;
    width: 150px !important;
    min-width: 150px !important;

  }
  .container_contact_name, .container_contact_id {
    span.contact_name, span.contact_id {
      text-decoration: underline;
      cursor: pointer;
    }
  }
  .container_company_id, .container_company_active, .container_company_type,
  .container_common_isPropertyPerson, .container_common_isEmergency,
  .container_common_isCircular,  .container_common_isOthers, .container_common_active,
  .container_tenancy_id{
    max-width: 120px;
    width: 120px;
    min-width: 120px;
  }
  .container_tenancy_buildingCode {
    min-width: 300px;
    width: 300px;
    max-width: 300px;
  }
  .container_contact_email,
  .container_contact_createdAt,
  .container_contact_updatedAt {
    max-width: 290px;
    width: 290px;
    min-width: 290px;
  }
  th, td {
    position: relative;
    height: 50px;
    max-height: 50px;
    min-height: 50px;
    min-width: 180px;
    text-align: left;
    border-left: 1px solid #E0E1E4;
    border-bottom: 1px solid #E0E1E4;
  }
  th:last-child,
  td:last-child {
    border-right: 1px solid #E0E1E4;
  }
  .results-table-content-container {
    overflow-y: auto;
    overflow-x: hidden;
    height: calc(100vh - 494px);
    span.company_name, span.tenancy_tradeName {
      text-decoration: underline;
    }
    th, td{
      font-family: Verdana;
      font-size: 14px;
      color: #0C3A58;
      font-weight: 300;
      text-transform: none;
      word-wrap: break-word;
      span.company_name, span.company_id, span.contact_id, span.contact_name, span.tenancy_tradeName {
        cursor: pointer;
        // border-bottom: 1px solid #0C3A58;
      }
      span.no_hyper_link {
        cursor: unset;
        border-bottom: 0;
        text-decoration: none;
      }
    }
    .popover-container:hover {
      cursor: not-allowed;
    }
    input[type='checkbox']:disabled {
      border-color: #e6e6e6 !important;
    }
    input[disabled] {
      pointer-events: none;
    }
  }
  .search-content {
    overflow: unset;
  }
  .search-content.type,
  .search-content.floor,
  .search-content.buildingCode {
    ::v-deep .hkl-select {
      height: auto;
      min-height: 38px;
      .ant-select-selection {
        height: 38px;
        overflow: auto;
      }
    }
  }
  .search-content.container_company_type {
    ::v-deep .hkl-select[mode='multiple']:not(.ant-select-disabled) {
      padding-right: 0;

      .ant-select-selection--multiple {
        width: 100%;
        padding-right: 40px;
      }

      .ant-select-selection__rendered {
        margin-right: 0;
      }

      .ant-select-selection__choice {
        min-width: 0;
        max-width: calc(100% - 4px);
      }
    }
  }
  .search-filter-input {
    position: absolute;
    top: 5px;
    bottom: 10px;
    right: 10px;
    left: 10px;
    display: flex;

    .input-area {
      display: flex;
      flex: 1;
      input {
        flex: 1;
      }
    }
  }

  .triangle-up {
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 10px solid #0C3A58;
    display: inline-block;
    margin-left: 5px;
  }

  .triangle-down {
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 10px solid #0C3A58;
    display: inline-block;
    margin-left: 5px;
  }

  .empty-results {
    opacity: 0.29;
    font-family: Verdana;
    font-weight: bold;
    font-size: 20px;
    color: #0C3A58;
    width: 100%;
    margin: 150px auto;
    position: absolute;
    text-align: center;
  }
}
</style>

<style lang='scss'>
.results-table {
  .input-area.search-area {
    width: 100%;
  }

  .select-area.company_type,
  .select-area.tenancy_buildingCode,
  .select-area.tenancy_floor,
  .select-area.tenancy_zone,
  .select-area.contact_prefix,
  .select-area.contact_roleType {
    .hkl-select {
      height: 100%;
      .ant-select-selection {
        height: 100%;
        overflow-x: hidden;
        overflow-y: auto;
      }
    }
  }

  .vdp-datepicker.columns {
    display: inline-block;
    height: 35px;
    width: 130px;
    .date-picker-class {
      height: 35px;
    }
    .vdp-datepicker__calendar-button {
      margin: 7px 5px;
    }
  }
  .vdp-datepicker__calendar.placement {
    bottom: auto !important;
  }
  .multiselect__content-wrapper {
    font-size: 0px;
    ul {
      font-size: 14px;
    }
  }
}
</style>
